/**
 * JWT Utility Functions
 * Helper functions for decoding and working with JWTs
 */

import { logger } from './logger';

export interface JWTPayload {
  sub: string; // Subject (mobile/email)
  userId: number; // User ID (added Jan 8, 2026)
  role: string; // User role
  name?: string; // User name (optional)
  mobile?: string; // Phone number
  permissions?: Record<string, boolean>; // Permission map
  exp: number; // Expiry timestamp
  iat: number; // Issued at timestamp
}

/**
 * Decode JWT token and extract payload
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }

    const base64Url = parts[1];
    if (!base64Url) {
      throw new Error('Invalid token format');
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    logger.error('Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) {
    return true;
  }

  // Check if token is expired (with 60-second buffer)
  const expiryTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 60 * 1000; // 60 seconds

  return currentTime >= (expiryTime - bufferTime);
};

/**
 * Get time until token expiry in seconds
 */
export const getTimeUntilExpiry = (token: string): number | null => {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) {
    return null;
  }

  const expiryTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  return Math.max(0, Math.floor((expiryTime - currentTime) / 1000));
};

/**
 * Extract user ID from JWT
 */
export const getUserIdFromToken = (token: string): number | null => {
  const payload = decodeJWT(token);
  return payload ? parseInt(payload.sub, 10) : null;
};

/**
 * Extract user role from JWT
 */
export const getUserRoleFromToken = (token: string): string | null => {
  const payload = decodeJWT(token);
  return payload?.role || null;
};

/**
 * Extract permissions from JWT
 */
export const getPermissionsFromToken = (token: string): Record<string, boolean> => {
  const payload = decodeJWT(token);
  return payload?.permissions || {};
};

/**
 * Check if token has specific permission
 */
export const hasPermission = (token: string, permission: string): boolean => {
  const permissions = getPermissionsFromToken(token);
  return permissions[permission] === true;
};
